import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useFlagsmith } from "@flagsmith/flagsmith/react";
import { AuthContext } from "../../providers/AuthProvider";
import FeatureFlags from "../feature-flagging/FeatureFlags";
import { setMigrated } from "./migrationStore";
import { deepLink } from "./deepLink";
import { NEW_HUB_URL } from "./constants";

// Cap the per-navigation flag re-pull so a flag flip still lands within one
// minute / one navigation without a getFlags network call on literally every
// route change. Employer changes always re-pull immediately (see below).
const FLAG_REFETCH_THROTTLE_MS = 60_000;

export interface MigrationStatusValue {
  isMigrated: boolean;
  // Cut-off: this employer no longer uses the old hub at all and is sent to Jem Hub.
  // Independent of isMigrated so a cohort can sit read-only for as long as it needs,
  // and so the cut-off can be reversed in seconds by flipping one flag.
  isRedirected: boolean;
  // Scheduled-but-not-yet-migrated: drives the dismissable "coming soon" banner.
  // Never true at the same time as isMigrated (migrated takes precedence).
  isScheduled: boolean;
  newHubUrl: string;
  deepLink: (oldPath: string) => string;
  // Signed-in user id, so the banner can remember a dismissal per user.
  // Undefined while the profile is still loading.
  userId: number | undefined;
}

const MigrationStatusContext = createContext<MigrationStatusValue>({
  isMigrated: false,
  isRedirected: false,
  isScheduled: false,
  newHubUrl: NEW_HUB_URL,
  deepLink,
  userId: undefined,
});

export const useMigrationStatusContext = (): MigrationStatusValue =>
  useContext(MigrationStatusContext);

export const MigrationStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useContext(AuthContext);
  const flagsmith = useFlagsmith();
  const location = useLocation();

  const employerId = auth?.employer?.id;
  const userId = auth?.id;

  // The flag lever is read via getFlags()->hasFeature so we control freshness:
  // Flagsmith's hasFeature is not reactive, and re-identify on an employer
  // switch is async. We (a) fail-open to false while (re)loading — so a stale
  // previous-employer flag can NEVER keep writes blocked after a switch (the
  // payroll-critical direction) — then (b) read the fresh value into state once
  // getFlags resolves, which re-renders and flips read-only on within one
  // navigation. Re-runs on employer switch and on every navigation.
  const [flags, setFlags] = useState({
    migrated: false,
    scheduled: false,
    redirect: false,
  });

  // Fail-open reset SYNCHRONOUSLY when the active employer changes (React's
  // "adjust state during render" pattern). Doing this in a passive effect would
  // let the first render for the new employer read the previous employer's stale
  // flags — the payroll-critical direction (falsely blocking writes on the new,
  // possibly non-migrated, employer). Resetting during render guarantees the
  // first committed render already sees fail-open false. Keyed on employer only
  // (not pathname) so the scheduled banner doesn't flicker on navigation.
  const [trackedEmployerId, setTrackedEmployerId] = useState(employerId);
  if (trackedEmployerId !== employerId) {
    setTrackedEmployerId(employerId);
    setFlags({ migrated: false, scheduled: false, redirect: false });
  }

  // Re-pull flags and read fresh values into state (this is what makes the
  // signal reactive). Always re-pull on employer change (payroll-critical);
  // on same-employer navigation, throttle to at most once per
  // FLAG_REFETCH_THROTTLE_MS so we don't hit Flagsmith on every click.
  // Last-known values are retained during the fetch and on transient error.
  const lastFetchAtRef = useRef(0);
  const lastEmployerRef = useRef(employerId);
  useEffect(() => {
    const employerChanged = lastEmployerRef.current !== employerId;
    lastEmployerRef.current = employerId;
    const now = Date.now();
    const shouldFetch =
      employerChanged || now - lastFetchAtRef.current >= FLAG_REFETCH_THROTTLE_MS;
    let cancelled = false;
    if (shouldFetch) {
      lastFetchAtRef.current = now;
      Promise.resolve(flagsmith.getFlags())
        .then(() => {
          if (!cancelled) {
            setFlags({
              migrated: flagsmith.hasFeature(FeatureFlags.JEM2_MIGRATED),
              scheduled: flagsmith.hasFeature(
                FeatureFlags.JEM2_MIGRATION_SCHEDULED
              ),
              redirect: flagsmith.hasFeature(FeatureFlags.JEM2_REDIRECT),
            });
          }
        })
        .catch(() => {
          /* transient fetch error — keep last-known values */
        });
    }
    return () => {
      cancelled = true;
    };
    // flagsmith is a stable singleton from useFlagsmith(); intentionally not a dep.
  }, [employerId, location.pathname]);

  // Migration is driven entirely by the Flagsmith flag (employerportal.jem2.migrated).
  const isMigrated = Boolean(flags.migrated);
  // Fails CLOSED, unlike isMigrated: until the flags have actually loaded this is false, so a
  // slow or failed Flagsmith call leaves the employer on the old hub rather than bouncing them
  // out of it. Losing a redirect for one navigation is recoverable; wrongly ejecting an employer
  // who is not cut off yet is not.
  const isRedirected = Boolean(flags.redirect);
  // Migrated wins: a migrated employer sees the "moved" banner, not "coming soon".
  const isScheduled = Boolean(flags.scheduled) && !isMigrated;

  // Mirror into the module store so the axios interceptors can read it.
  useEffect(() => {
    setMigrated(isMigrated);
  }, [isMigrated]);

  const value = useMemo(
    () => ({
      isMigrated,
      isRedirected,
      isScheduled,
      newHubUrl: NEW_HUB_URL,
      deepLink,
      userId,
    }),
    [isMigrated, isRedirected, isScheduled, userId]
  );

  return (
    <MigrationStatusContext.Provider value={value}>
      {children}
    </MigrationStatusContext.Provider>
  );
};

export default MigrationStatusProvider;
